/* eslint-disable react/no-unused-prop-types */
import { ReactNode, useCallback, useMemo } from 'react';
import { query, where } from 'firebase/firestore';
import { useReduxForm } from '@rainbow-modules/hooks';
import { PicklistValue } from '@rainbow-modules/listview/lib/components/AuditLogs/types';
import { Option } from 'react-rainbow-components';
import useResponders from 'data/firestore/agent/llm/useCollection';
import useHttpQuery from 'data/firestore/useHttpQuery';
import Script from 'components/icons/script';
import { ACTIONS_BASE_URL } from '../../constants';
import { CallScriptValue, CallDirection } from '../CallResponderPicker/types';
import { StyledPicklist } from './styled';

interface Workflow {
    id: string;
    name: string;
    enabled: boolean;
    triggerType: string | null;
}

interface CallScriptPickerProps {
    id?: string;
    className?: string;
    agentId?: string;
    name?: string;
    label?: string;
    tabIndex?: number | string;
    placeholder?: string;
    error?: ReactNode;
    required?: boolean;
    value?: CallScriptValue;
    direction?: [CallDirection, ...CallDirection[]];
    onChange?: (value: CallScriptValue) => void;
}

const directionToFeatureSchemaMap: Record<CallDirection, string> = {
    inbound: 'system.call.incoming',
    outbound: 'system.call.outbound',
};

const PERSONA_PREFIX = 'persona:';
const WORKFLOW_PREFIX = 'workflow:';

const CallScriptPicker = (props: CallScriptPickerProps) => {
    const {
        value: valueInProps,
        agentId = '',
        onChange = () => {},
        id,
        className,
        name,
        label,
        tabIndex,
        placeholder,
        direction = ['inbound', 'outbound'],
        error,
        required,
    } = useReduxForm(props);

    const { data: responders, isLoading: isLoadingResponders } = useResponders(agentId, {
        listQueryFn: (ref) => {
            const schemas = Array.from(
                new Set(direction.map((dir) => directionToFeatureSchemaMap[dir])),
            );
            return query(ref, where('featureSchema', 'in', schemas));
        },
        disabled: !agentId,
        track: [agentId],
    });

    const {
        data: workflowData, isLoading: isLoadingWorkflows,
    } = useHttpQuery<{ data: Workflow[] }>({
        pathname: `/workflows?agentId=${agentId}`,
        server: ACTIONS_BASE_URL,
        queryOptions: {
            enabled: Boolean(agentId),
        },
    });

    const workflows = (workflowData?.data ?? []).filter(
        (w) => w.triggerType === 'twilio-voice-trigger',
    );

    const isLoading = isLoadingResponders || isLoadingWorkflows;

    const pickListvalue = useMemo(() => {
        if (isLoading || !valueInProps) return undefined;
        const { type, id: valueId } = valueInProps;
        if (type === 'persona') {
            const found = responders.find((r) => r.id === valueId);
            if (found) {
                return {
                    label: found.name,
                    name: `${PERSONA_PREFIX}${found.id}`,
                    value: `${PERSONA_PREFIX}${found.id}`,
                    icon: <Script />,
                };
            }
        } else {
            const found = workflows.find((w) => w.id === valueId);
            if (found) {
                return {
                    label: found.name,
                    name: `${WORKFLOW_PREFIX}${found.id}`,
                    value: `${WORKFLOW_PREFIX}${found.id}`,
                    icon: <Script />,
                };
            }
        }
        return undefined;
    }, [isLoading, responders, workflows, valueInProps]);

    const handleOnChange = useCallback(
        (value: PicklistValue) => {
            const raw = value.value as string;
            if (raw.startsWith(PERSONA_PREFIX)) {
                onChange({ type: 'persona', id: raw.slice(PERSONA_PREFIX.length) });
            } else if (raw.startsWith(WORKFLOW_PREFIX)) {
                onChange({ type: 'workflow', id: raw.slice(WORKFLOW_PREFIX.length) });
            }
        },
        [onChange],
    );

    return (
        <StyledPicklist
            id={id}
            name={name}
            className={className}
            label={label}
            labelAlignment="left"
            tabIndex={tabIndex}
            required={required}
            placeholder={placeholder}
            isLoading={isLoading}
            value={pickListvalue}
            onChange={handleOnChange}
            error={error}
        >
            <Option label="Workflows" variant="header" />
            {workflows.map((workflow) => (
                <Option
                    key={`workflow_${workflow.id}`}
                    label={workflow.name}
                    name={`${WORKFLOW_PREFIX}${workflow.id}`}
                    value={`${WORKFLOW_PREFIX}${workflow.id}`}
                    searchableText={workflow.name}
                    icon={<Script />}
                />
            ))}
            <Option label="AI Scripts" variant="header" />
            {responders.map((responder) => (
                <Option
                    key={`responder_${responder.id}`}
                    label={responder.name}
                    name={`${PERSONA_PREFIX}${responder.id}`}
                    value={`${PERSONA_PREFIX}${responder.id}`}
                    searchableText={responder.name}
                    icon={<Script />}
                />
            ))}
        </StyledPicklist>
    );
};

export default CallScriptPicker;
